export interface PlanFeature {
  feature: string;
  label: string;
  starter: boolean | number;
  professional: boolean | number;
  business: boolean | number;
  enterprise: boolean | number;
}

export interface PlanDef {
  id: string;
  name: string;
  price: number; // monthly USD
  yearlyPrice: number;
  description: string;
  highlight?: boolean;
  maxUsers: number;
  maxCustomers: number;
  maxInvoices: number; // per month
  maxEmployees: number;
}

export const PLANS: PlanDef[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 0,
    yearlyPrice: 0,
    description: 'For freelancers and solo accountants',
    maxUsers: 1,
    maxCustomers: 20,
    maxInvoices: 50,
    maxEmployees: 0,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 29,
    yearlyPrice: 290,
    description: 'For small businesses and growing teams',
    highlight: true,
    maxUsers: 5,
    maxCustomers: 200,
    maxInvoices: 500,
    maxEmployees: 25,
  },
  {
    id: 'business',
    name: 'Business',
    price: 79,
    yearlyPrice: 790,
    description: 'For mid-size companies with advanced needs',
    maxUsers: 25,
    maxCustomers: -1,
    maxInvoices: -1,
    maxEmployees: 200,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 199,
    yearlyPrice: 1990,
    description: 'For large organizations with full control',
    maxUsers: -1,
    maxCustomers: -1,
    maxInvoices: -1,
    maxEmployees: -1,
  },
];

export const PLAN_FEATURES: PlanFeature[] = [
  { feature: 'customers', label: 'Customer Management', starter: true, professional: true, business: true, enterprise: true },
  { feature: 'invoices', label: 'Invoicing', starter: true, professional: true, business: true, enterprise: true },
  { feature: 'vendors', label: 'Vendor Management', starter: true, professional: true, business: true, enterprise: true },
  { feature: 'expenses', label: 'Expense Tracking', starter: true, professional: true, business: true, enterprise: true },
  { feature: 'accounts', label: 'Chart of Accounts', starter: true, professional: true, business: true, enterprise: true },
  { feature: 'journal_entries', label: 'Journal Entries', starter: false, professional: true, business: true, enterprise: true },
  { feature: 'reconciliation', label: 'Bank Reconciliation', starter: false, professional: true, business: true, enterprise: true },
  { feature: 'recurring', label: 'Recurring Transactions', starter: false, professional: true, business: true, enterprise: true },
  { feature: 'period_closing', label: 'Period Closing', starter: false, professional: true, business: true, enterprise: true },
  { feature: 'payroll', label: 'Payroll', starter: false, professional: true, business: true, enterprise: true },
  { feature: 'fixed_assets', label: 'Fixed Assets', starter: false, professional: false, business: true, enterprise: true },
  { feature: 'inventory', label: 'Inventory Management', starter: false, professional: false, business: true, enterprise: true },
  { feature: 'reports', label: 'Financial Reports', starter: true, professional: true, business: true, enterprise: true },
  { feature: 'ai_assistant', label: 'AI Assistant', starter: 5, professional: 50, business: 200, enterprise: -1 },
  { feature: 'data_management', label: 'Data Import/Export', starter: false, professional: true, business: true, enterprise: true },
  { feature: 'api_access', label: 'API Access', starter: false, professional: false, business: false, enterprise: true },
];

export function getPlan(planId: string): PlanDef {
  return PLANS.find(p => p.id === planId) || PLANS[0];
}

export function isFeatureEnabled(planId: string, feature: string): boolean {
  const row = PLAN_FEATURES.find(f => f.feature === feature);
  if (!row) return true;
  const val = row[planId as keyof PlanFeature];
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val !== 0;
  return true;
}

export function getFeatureLimit(planId: string, feature: string): number {
  const row = PLAN_FEATURES.find(f => f.feature === feature);
  if (!row) return -1;
  const val = row[planId as keyof PlanFeature];
  if (typeof val === 'number') return val;
  return val ? -1 : 0;
}

export function formatLimit(val: number | boolean): string {
  if (val === true) return '✓';
  if (val === false || val === 0) return '—';
  if (val === -1) return 'Unlimited';
  return String(val);
}
